/**
 * Payout Health Dashboard
 * Admin view: payout statuses, webhook events, failed/needs-attention, LA status list.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import {
  RefreshCw, Loader2, AlertCircle, CheckCircle2, Clock,
  Zap, Link2, BarChart3, Webhook, XCircle, ChevronDown, ChevronRight,
  ShieldCheck, Settings, Play,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../components/ui/sonner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = n => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = iso => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const dateLabel = iso => {
  if (!iso) return 'Unknown date';
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const STATUS_BADGE = {
  settled:        'bg-emerald-100 text-emerald-800',
  computed:       'bg-blue-100 text-blue-700',
  transfer_failed:'bg-red-100 text-red-700',
  failed:         'bg-red-100 text-red-700',
  unknown:        'bg-amber-100 text-amber-700',
};

const LA_STATE_BADGE = {
  linked:                'bg-emerald-100 text-emerald-800',
  blocked_missing_fields:'bg-amber-100 text-amber-700',
  last_attempt_failed:   'bg-red-100 text-red-700',
  ready_never_attempted: 'bg-blue-100 text-blue-700',
};

function StatusPill({ status }) {
  const cls = STATUS_BADGE[status] || 'bg-gray-100 text-gray-500';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status || 'unknown'}</span>;
}

function LAStatePill({ state }) {
  const cls = LA_STATE_BADGE[state] || 'bg-gray-100 text-gray-500';
  const label = {
    linked: 'Linked',
    blocked_missing_fields: 'Missing Fields',
    last_attempt_failed: 'Failed',
    ready_never_attempted: 'Ready',
  }[state] || state || 'Not started';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{label}</span>;
}

// Group payouts by date (YYYY-MM-DD from updated_at)
function groupByDate(payouts) {
  const groups = {};
  for (const p of payouts) {
    const key = (p.updated_at || p.created_at || '').slice(0, 10);
    if (!groups[key]) groups[key] = [];
    groups[key].push(p);
  }
  // Return sorted descending
  return Object.entries(groups)
    .sort(([a], [b]) => (b > a ? 1 : -1))
    .map(([date, items]) => ({ date, items }));
}

// ─── Summary Cards ─────────────────────────────────────────────────────────────

function SummaryCards({ summary, pidHealth }) {
  const sb = summary?.status_breakdown || {};
  const pidMissing = pidHealth?.paid_orders_missing_pid ?? null;
  const sweeper = pidHealth?.sweeper;
  const cards = [
    {
      label: 'Pending Payouts',
      value: fmt(summary?.pending_amount),
      sub: `${summary?.pending_count || 0} orders`,
      icon: Clock,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Total Settled',
      value: fmt(summary?.total_settled),
      sub: `${sb.settled?.count || 0} payouts`,
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bg: 'bg-emerald-50',
    },
    {
      label: 'Needs Attention',
      value: `${(sb.unknown?.count || 0) + (sb.transfer_failed?.count || 0) + (sb.failed?.count || 0)}`,
      sub: 'unknown / failed',
      icon: AlertCircle,
      color: 'text-amber-600',
      bg: 'bg-amber-50',
    },
    {
      label: 'Webhook Events',
      value: Object.values(summary?.webhook_event_counts || {}).reduce((a, b) => a + b, 0),
      sub: `${Object.keys(summary?.webhook_event_counts || {}).length} types`,
      icon: Webhook,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    {
      label: 'Payment IDs Missing',
      value: pidMissing === null ? '—' : String(pidMissing),
      sub: sweeper?.last_run
        ? `sweeper: ${sweeper.fixed || 0} fixed · paid-hooks rcvd: ${pidHealth?.orders_paid_webhooks_received ?? '—'}`
        : `paid-hooks rcvd: ${pidHealth?.orders_paid_webhooks_received ?? '—'} · sweeper pending`,
      icon: AlertCircle,
      color: pidMissing > 0 ? 'text-red-600' : 'text-emerald-600',
      bg: pidMissing > 0 ? 'bg-red-50' : 'bg-emerald-50',
      testId: 'pid-missing-card',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
      {cards.map(c => (
        <div key={c.label} data-testid={c.testId} className="bg-white border border-gray-200 rounded-xl p-4">
          <div className={`w-9 h-9 ${c.bg} rounded-lg flex items-center justify-center mb-3`}>
            <c.icon className={`w-5 h-5 ${c.color}`} />
          </div>
          <p className="text-2xl font-bold text-gray-900">{c.value}</p>
          <p className="text-sm text-gray-500 mt-0.5">{c.label}</p>
          <p className="text-xs text-gray-400 mt-0.5">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Payouts Tab (grouped by date) ─────────────────────────────────────────────

function PayoutsTab({ payouts }) {
  const [expanded, setExpanded] = useState({});
  const groups = groupByDate(payouts);

  if (!groups.length) {
    return <EmptyState icon={BarChart3} title="No payout records" sub="No data in selected period." />;
  }

  return (
    <div className="space-y-4">
      {groups.map(({ date, items }) => {
        const isOpen = expanded[date] !== false; // default open
        const total = items.reduce((s, p) => s + (p.net_payout || 0), 0);
        return (
          <div key={date} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            {/* Date header */}
            <button
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              onClick={() => setExpanded(e => ({ ...e, [date]: !isOpen }))}
              data-testid={`date-group-${date}`}
            >
              <div className="flex items-center gap-3">
                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                <span className="font-semibold text-sm text-gray-700">{dateLabel(date)}</span>
                <span className="text-xs text-gray-400">{items.length} payout{items.length !== 1 ? 's' : ''}</span>
              </div>
              <span className="text-sm font-semibold text-gray-700">{fmt(total)}</span>
            </button>

            {/* Rows */}
            {isOpen && (
              <div className="divide-y divide-gray-100">
                {items.map((p, i) => (
                  <div key={p.id || i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-sm" data-testid={`payout-row-${p.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 truncate">{p.order_number || p.order_id?.slice(0, 16) || '—'}</p>
                      <p className="text-xs text-gray-400 truncate">{p.vendor_name || p.vendor_id?.slice(0, 16) || '—'}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-3">
                      <StatusPill status={p.status} />
                      {p.razorpay_transfer_id && (
                        <span className="hidden sm:inline text-xs font-mono text-gray-400 max-w-[90px] truncate">{p.razorpay_transfer_id}</span>
                      )}
                      <span className="font-semibold text-gray-700">{fmt(p.net_payout)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Failed / Needs Attention Tab ──────────────────────────────────────────────

function FailedTab({ payouts }) {
  if (!payouts.length) {
    return (
      <div className="text-center py-16" data-testid="failed-empty-state">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <p className="text-sm font-semibold text-gray-700">All clear</p>
        <p className="text-xs text-gray-400 mt-1">No failed or unknown-status payouts.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="failed-payouts-list">
      {payouts.map((p, i) => (
        <div key={p.id || i} className="border border-red-100 rounded-xl bg-red-50/40 p-4" data-testid={`failed-payout-${p.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-gray-900">{p.order_number || p.order_id?.slice(0, 16) || '—'}</span>
                <StatusPill status={p.status} />
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{p.vendor_name || p.vendor_id?.slice(0, 20) || '—'}</p>
              {p.transfer_error && (
                <p className="text-xs text-red-600 mt-1.5 bg-red-50 border border-red-100 rounded px-2 py-1">
                  {typeof p.transfer_error === 'string' ? p.transfer_error : JSON.stringify(p.transfer_error)}
                </p>
              )}
            </div>
            <div className="text-right shrink-0">
              <p className="font-bold text-gray-900">{fmt(p.net_payout)}</p>
              <p className="text-xs text-gray-400 mt-0.5">{fmtDate(p.updated_at)}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Webhook Events Tab ────────────────────────────────────────────────────────

function WebhookEventTag({ event }) {
  const color = event.startsWith('transfer.') ? 'bg-blue-100 text-blue-700'
    : event.startsWith('payment') ? 'bg-emerald-100 text-emerald-700'
    : event.startsWith('settlement') ? 'bg-purple-100 text-purple-700'
    : 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded text-xs font-mono font-medium ${color}`}>{event}</span>;
}

function WebhooksTab({ webhooks, eventCounts }) {
  if (!webhooks.length) {
    return <EmptyState icon={Webhook} title="No webhook events" sub="No Razorpay webhooks received yet." />;
  }
  return (
    <div className="space-y-5" data-testid="webhooks-tab">
      {/* Event type summary */}
      {Object.keys(eventCounts).length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Event Types</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(eventCounts).map(([evt, cnt]) => (
              <span key={evt} className="flex items-center gap-1.5 bg-white border border-gray-200 rounded-full px-3 py-1 text-xs">
                <span className="font-mono text-gray-600">{evt}</span>
                <span className="bg-gray-200 text-gray-700 rounded-full px-1.5 py-0.5 text-xs font-semibold">{cnt}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Event log */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white divide-y divide-gray-100">
        {webhooks.map((wh, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-sm" data-testid={`webhook-event-${i}`}>
            <div className="min-w-0 flex-1 flex items-center gap-3">
              <WebhookEventTag event={wh.event} />
              <div className="min-w-0">
                {wh.entity_id && <span className="text-xs font-mono text-gray-400 block truncate">{wh.entity_id}</span>}
                {wh.error && <span className="text-xs text-red-500 block truncate">{wh.error}</span>}
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0 ml-2">
              {wh.status && <span className="text-xs text-gray-500">{wh.status}</span>}
              {wh.amount > 0 && <span className="text-sm font-semibold text-gray-700">{fmt(wh.amount)}</span>}
              <div className="text-right">
                <p className="text-xs text-gray-400">{fmtDate(wh.received_at)}</p>
                <div className="flex items-center justify-end gap-1 mt-0.5">
                  {wh.verified
                    ? <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    : <XCircle className="w-3 h-3 text-red-400" />}
                  <span className={`text-xs ${wh.http_status === 200 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {wh.http_status || '—'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Admin panel: self-test + secret update */}
      <WebhookAdminPanel />
    </div>
  );
}

// ─── Webhook Admin Panel ───────────────────────────────────────────────────────

function WebhookAdminPanel() {
  const { apiCall } = useAuth();
  const [status, setStatus]       = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [secret, setSecret]       = useState('');
  const [savingSecret, setSavingSecret] = useState(false);
  const [open, setOpen]           = useState(true);

  const loadStatus = async () => {
    setLoadingStatus(true);
    try { setStatus(await apiCall('/api/webhook-manager/razorpay/status')); }
    catch (e) { /* status fetch failed */ }
    finally { setLoadingStatus(false); }
  };

  useEffect(() => { loadStatus(); }, []);

  const runSelfTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await apiCall('/api/webhook-manager/razorpay/self-test', { method: 'POST' });
      setTestResult(r);
      if (r?.ok) toast.success(`Self-test passed (${r.http_status})`);
      else toast.error(`Self-test failed: HTTP ${r?.http_status}`);
    } catch (e) { toast.error(e.message); }
    finally { setTesting(false); }
  };

  const saveSecret = async () => {
    if (!secret.trim()) return;
    setSavingSecret(true);
    try {
      await apiCall('/api/webhook-manager/razorpay/secret', {
        method: 'PUT',
        body: JSON.stringify({ secret }),
      });
      toast.success('Webhook secret updated');
      setSecret('');
      loadStatus();
    } catch (e) { toast.error(e.message); }
    finally { setSavingSecret(false); }
  };

  return (
    <div className="border border-gray-200 rounded-xl bg-white overflow-hidden" data-testid="webhook-admin-panel">
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
        onClick={() => { setOpen(o => !o); if (!open && !status) loadStatus(); }}
        data-testid="webhook-admin-toggle"
      >
        <div className="flex items-center gap-2">
          <Settings className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-semibold text-gray-700">Webhook Admin</span>
          <span className="text-xs text-gray-400">· secret, self-test</span>
        </div>
        {open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 p-4 space-y-5">
          {/* Status */}
          {loadingStatus && <div className="flex items-center gap-2 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" />Loading status…</div>}
          {status && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {[
                { label: 'Active',       value: status.is_active ? 'Yes' : 'No',              ok: status.is_active },
                { label: 'Secret set',   value: status.secret_configured ? 'Yes' : 'No',      ok: status.secret_configured },
                { label: 'Total rcvd',   value: status.total_received ?? '—',                  ok: true },
                { label: 'Last event',   value: status.last_event_at ? fmtDate(status.last_event_at) : 'Never', ok: !!status.last_event_at },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 rounded-lg p-2.5">
                  <p className={`text-sm font-bold ${s.ok ? 'text-gray-800' : 'text-red-500'}`}>{s.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {/* Self-test */}
          <div className="flex items-center gap-3 flex-wrap">
            <Button variant="outline" size="sm" onClick={runSelfTest} disabled={testing} data-testid="webhook-self-test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
              {testing ? 'Testing…' : 'Run Self-Test'}
            </Button>
            {testResult && (
              <span className={`text-xs font-medium px-2 py-1 rounded ${testResult.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                {testResult.ok ? `Passed · HTTP ${testResult.http_status}` : `Failed · HTTP ${testResult.http_status || '?'}`}
              </span>
            )}
          </div>

          {/* Update secret */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Update HMAC Secret</p>
            <p className="text-xs text-gray-400">Paste the secret from Razorpay Dashboard → Webhooks → Edit → Secret</p>
            <div className="flex gap-2">
              <input
                type="password"
                value={secret}
                onChange={e => setSecret(e.target.value)}
                placeholder="Razorpay webhook secret…"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                data-testid="webhook-secret-input"
              />
              <Button size="sm" onClick={saveSecret} disabled={savingSecret || !secret.trim()} data-testid="webhook-secret-save-btn">
                {savingSecret ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Linked Accounts Tab ───────────────────────────────────────────────────────

function LinkedAccountsTab({ laVendors }) {
  const [search, setSearch] = useState('');
  const filtered = laVendors.filter(v =>
    !search ||
    (v.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (v.la_id || '').includes(search)
  );

  const stateGroups = {
    linked:                filtered.filter(v => v.la_state === 'linked'),
    ready_never_attempted: filtered.filter(v => v.la_state === 'ready_never_attempted'),
    blocked_missing_fields:filtered.filter(v => v.la_state === 'blocked_missing_fields'),
    last_attempt_failed:   filtered.filter(v => v.la_state === 'last_attempt_failed'),
    other:                 filtered.filter(v => !v.la_state),
  };

  const stateLabels = {
    linked: { label: 'Linked', icon: CheckCircle2, color: 'text-emerald-600' },
    ready_never_attempted: { label: 'Ready (Pending Activation)', icon: Clock, color: 'text-blue-600' },
    blocked_missing_fields: { label: 'Missing Profile Fields', icon: AlertCircle, color: 'text-amber-600' },
    last_attempt_failed: { label: 'Creation Failed', icon: XCircle, color: 'text-red-600' },
    other: { label: 'Not Started', icon: Link2, color: 'text-gray-400' },
  };

  if (!laVendors.length) {
    return <EmptyState icon={Link2} title="No vendors found" sub="No vendor accounts in system." />;
  }

  return (
    <div className="space-y-5" data-testid="la-status-tab">
      <input
        type="text"
        placeholder="Search by name, email or account ID…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
        data-testid="la-search-input"
      />

      {Object.entries(stateGroups).map(([state, vendors]) => {
        if (!vendors.length) return null;
        const { label, icon: Icon, color } = stateLabels[state];
        return (
          <div key={state} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200">
              <Icon className={`w-4 h-4 ${color}`} />
              <span className="text-sm font-semibold text-gray-700">{label}</span>
              <span className="ml-auto bg-gray-200 text-gray-600 rounded-full px-2 py-0.5 text-xs font-bold">{vendors.length}</span>
            </div>
            <div className="divide-y divide-gray-100">
              {vendors.map((v, i) => (
                <div key={v.vendor_id || i} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-gray-50 text-sm" data-testid={`la-vendor-row-${v.vendor_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 truncate">{v.name || '—'}</p>
                    <p className="text-xs text-gray-400 truncate">{v.email}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    {v.la_id
                      ? <p className="text-xs font-mono text-gray-500 truncate max-w-[120px]">{v.la_id}</p>
                      : <p className="text-xs text-gray-300">no account</p>}
                    {v.la_error && (
                      <p className="text-xs text-red-500 truncate max-w-[180px]" title={v.la_error}>{v.la_error}</p>
                    )}
                  </div>
                  <LAStatePill state={v.la_state} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Payment ID Backfill Card ──────────────────────────────────────────────────

function PaymentIDBackfillCard({ apiCall }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [razorpayRunning, setRazorpayRunning] = useState(false);
  const [razorpayResult, setRazorpayResult] = useState(null);
  const pollRef = useCallback(() => {}, []);

  const loadStatus = useCallback(async () => {
    try {
      const res = await apiCall('/api/orders/backfill-status');
      setStatus(res);
    } catch { /* silent */ }
  }, [apiCall]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll every 4s while a job is running
  useEffect(() => {
    if (status?.status !== 'running') return;
    const id = setInterval(loadStatus, 4000);
    return () => clearInterval(id);
  }, [status?.status, loadStatus]);

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await apiCall('/api/orders/backfill-all-payment-ids', { method: 'POST' });
      if (res?.ok) {
        toast.success(`Backfill started — ${res.total_missing} orders to process`);
        setStatus(s => ({ ...s, status: 'running', job_id: res.job_id, total_missing: res.total_missing, processed: 0, found: 0 }));
        setTimeout(loadStatus, 2000);
      } else {
        toast.error(res?.detail || 'Could not start backfill');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStarting(false);
    }
  };

  const handleRazorpayMatch = async () => {
    setRazorpayRunning(true);
    setRazorpayResult(null);
    try {
      const res = await apiCall('/api/orders/bulk-fetch-payment-ids-via-razorpay', { method: 'POST' });
      if (res?.ok && res.summary) {
        setRazorpayResult(res.summary);
        const s = res.summary;
        if (s.found > 0) {
          toast.success(`Razorpay match: ${s.found} recovered, ${s.not_found} still missing (of ${s.scanned})`);
        } else {
          toast.error(`Razorpay match: no new IDs recovered (${s.scanned} scanned, ${s.not_found} still missing)`);
        }
        // Refresh the top-level missing count
        loadStatus();
      } else {
        toast.error(res?.detail || 'Razorpay match failed');
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setRazorpayRunning(false);
    }
  };

  const isRunning = status?.status === 'running';
  const pct = status?.total_missing > 0 ? Math.round((status.processed / status.total_missing) * 100) : 0;
  const missingNow = status?.total_missing_now ?? '—';

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white" data-testid="payment-id-backfill-card">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <p className="text-sm font-semibold text-gray-700">Payment ID Backfill</p>
        <button onClick={loadStatus} className="text-gray-400 hover:text-gray-600 transition-colors" title="Refresh status">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-4 py-4 space-y-4">
        {/* Missing count */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">Orders missing <span className="font-mono text-xs">pay_xxx</span></span>
          <span className={`font-bold ${missingNow > 0 ? 'text-amber-600' : 'text-emerald-600'}`} data-testid="backfill-missing-count">
            {missingNow}
          </span>
        </div>

        {/* Last job status */}
        {status && status.status !== 'never_run' && (
          <div className="text-xs space-y-1 bg-gray-50 rounded-lg p-3 border border-gray-100">
            <div className="flex justify-between">
              <span className="text-gray-500">Last job</span>
              <span className={`font-semibold ${status.status === 'completed' ? 'text-emerald-600' : status.status === 'running' ? 'text-blue-600' : 'text-red-600'}`}>
                {status.status}
              </span>
            </div>
            {status.status === 'running' && (
              <div>
                <div className="flex justify-between text-gray-500 mb-1">
                  <span>Progress</span>
                  <span>{status.processed}/{status.total_missing} ({pct}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}
            {status.status === 'completed' && (
              <div className="flex justify-between text-gray-500">
                <span>Found / Still missing / Errors</span>
                <span className="font-mono">{status.found} / {status.still_missing} / {status.errors}</span>
              </div>
            )}
            {status.started_at && (
              <div className="flex justify-between text-gray-400">
                <span>Started</span>
                <span>{fmtDate(status.started_at)}</span>
              </div>
            )}
          </div>
        )}

        {/* Action button */}
        <button
          onClick={handleStart}
          disabled={isRunning || starting || missingNow === 0}
          data-testid="start-backfill-btn"
          className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {isRunning ? (
            <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Running…</>
          ) : (
            <><Play className="w-3.5 h-3.5" /> Backfill {missingNow > 0 ? `${missingNow} Orders` : '(All Up to Date)'}</>
          )}
        </button>
        <p className="text-xs text-gray-400 text-center">
          Fetches <span className="font-mono">pay_xxx</span> from Shopify Transactions API for each order. Runs in background, ~15 orders/batch.
        </p>

        {/* Razorpay reverse-lookup — last-resort recovery when Shopify txns
            never carried the pay_* id (newer Razorpay-Shopify gateway). */}
        <div className="pt-3 mt-3 border-t border-gray-100 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-700">Fallback · Razorpay Transaction Match</p>
            {razorpayResult && (
              <span className="text-[10px] font-mono text-gray-500" data-testid="razorpay-match-summary">
                {razorpayResult.found}✓ / {razorpayResult.not_found}✗ / {razorpayResult.errors}⚠ (of {razorpayResult.scanned})
              </span>
            )}
          </div>
          <button
            onClick={handleRazorpayMatch}
            disabled={razorpayRunning || isRunning || missingNow === 0}
            data-testid="razorpay-match-btn"
            className="w-full flex items-center justify-center gap-2 text-sm px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {razorpayRunning ? (
              <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Matching against Razorpay…</>
            ) : (
              <><Zap className="w-3.5 h-3.5" /> Try Razorpay Match {missingNow > 0 ? `(${Math.min(50, missingNow)} orders)` : ''}</>
            )}
          </button>
          <p className="text-xs text-gray-400 text-center">
            Queries Razorpay <span className="font-mono">/v1/payments</span> in a ±90-min window per order, matches by <span className="font-mono">notes.shopify_order_id</span> (primary) or unique amount (fallback). Capped at 50 orders/run.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Status Breakdown Tab ──────────────────────────────────────────────────────

function OverviewTab({ summary, payouts, apiCall }) {
  const sb = summary?.status_breakdown || {};
  const total = Object.values(sb).reduce((s, v) => s + v.count, 0);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      {/* Payment ID Backfill */}
      <PaymentIDBackfillCard apiCall={apiCall} />

      {/* Status breakdown table */}
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <p className="text-sm font-semibold text-gray-700">Status Breakdown</p>
        </div>
        <div className="divide-y divide-gray-100">
          {Object.entries(sb).sort(([, a], [, b]) => b.count - a.count).map(([status, stats]) => (
            <div key={status} className="flex items-center justify-between px-4 py-3 text-sm">
              <div className="flex items-center gap-3">
                <StatusPill status={status} />
                <span className="text-gray-600">{stats.count} records</span>
              </div>
              <span className="font-semibold text-gray-800">{fmt(stats.amount)}</span>
            </div>
          ))}
          {!Object.keys(sb).length && (
            <div className="px-4 py-8 text-center text-sm text-gray-400">No payout data yet.</div>
          )}
        </div>
      </div>

      {/* Webhook event types */}
      {Object.keys(summary?.webhook_event_counts || {}).length > 0 && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-700">Webhook Events Received</p>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(summary.webhook_event_counts).map(([evt, cnt]) => (
              <div key={evt} className="flex items-center justify-between px-4 py-3 text-sm">
                <span className="font-mono text-gray-600">{evt}</span>
                <span className="font-semibold text-gray-800">{cnt}×</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Empty State Helper ────────────────────────────────────────────────────────

function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div className="text-center py-16 text-gray-400">
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-30" />
      <p className="text-sm font-semibold text-gray-500">{title}</p>
      {sub && <p className="text-xs mt-1">{sub}</p>}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: 'Overview',         icon: BarChart3  },
  { id: 'payouts',    label: 'Payouts',           icon: Zap        },
  { id: 'failed',     label: 'Needs Attention',   icon: AlertCircle},
  { id: 'webhooks',   label: 'Webhook Events',    icon: Webhook    },
  { id: 'accounts',   label: 'Linked Accounts',   icon: Link2      },
];

export default function PayoutHealthDashboard() {
  const { apiCall } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [days,      setDays]      = useState(30);
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiCall(`/api/razorpay-route/payout-health?days=${days}`);
      setData(res);
    } catch (e) {
      setError(e?.message || 'Failed to load payout health data');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const failedCount = data?.failed_payouts?.length || 0;
  const webhookCount = data?.recent_webhooks?.length || 0;

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50" data-testid="payout-health-page">

        {/* Page header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="max-w-6xl mx-auto flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Payout Health Monitor</h1>
              <p className="text-sm text-gray-500 mt-0.5">Razorpay Route transfers, webhook events and linked account statuses</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={days}
                onChange={e => setDays(Number(e.target.value))}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-300"
                data-testid="days-filter"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 365 days</option>
              </select>
              <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">

          {/* Loading */}
          {loading && !data && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="error-state">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {data && (
            <>
              {/* Summary cards */}
              <SummaryCards summary={data.summary} pidHealth={data.payment_id_health} />

              {/* Tabs */}
              <div className="border-b border-gray-200 bg-white rounded-t-xl overflow-hidden">
                <nav className="flex overflow-x-auto" data-testid="health-tabs">
                  {TABS.map(tab => {
                    const count = tab.id === 'failed' ? failedCount
                      : tab.id === 'webhooks' ? webhookCount
                      : tab.id === 'payouts' ? (data.recent_payouts?.length || 0)
                      : tab.id === 'accounts' ? (data.la_vendors?.length || 0)
                      : null;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        data-testid={`tab-${tab.id}`}
                        className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                          activeTab === tab.id
                            ? 'border-gray-900 text-gray-900'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <tab.icon className="w-4 h-4" />
                        {tab.label}
                        {count !== null && (
                          <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                            tab.id === 'failed' && count > 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                          }`}>{count}</span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>

              <div className="pb-8" data-testid="tab-content">
                {activeTab === 'overview'  && <OverviewTab summary={data.summary} payouts={data.recent_payouts || []} apiCall={apiCall} />}
                {activeTab === 'payouts'   && <PayoutsTab payouts={data.recent_payouts || []} />}
                {activeTab === 'failed'    && <FailedTab payouts={data.failed_payouts || []} />}
                {activeTab === 'webhooks'  && <WebhooksTab webhooks={data.recent_webhooks || []} eventCounts={data.summary?.webhook_event_counts || {}} />}
                {activeTab === 'accounts'  && <LinkedAccountsTab laVendors={data.la_vendors || []} />}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
